// src/components/QuizWrapper.jsx - MATCHES CSS FILES
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuizContext } from '../hooks/useQuizContext';
import QuestionCard from './QuestionCard';

/**
 * QuizWrapper
 * - renders current question
 * - navigation (prev/next) and submit button
 * - NO progress bar (handled by QuizDashboard phase header)
 *
 * Props:
 *  - onSubmit: function called when Submit is clicked
 *  - onPhaseComplete: function called when phase complete button is clicked (optional)
 *  - submitting: boolean to disable submit while network request is in progress (optional)
 *  - submitError: string error message to show (optional)
 *  - hideSubmit: boolean to hide the Submit button (used when phase complete button shows) (optional)
 *  - showPhaseComplete: boolean to show "Next Phase" button instead of Submit (optional)
 *  - phaseCompleteText: text for phase complete button (optional, defaults to "Next Phase")
 */
const QuizWrapper = ({ 
  onSubmit, 
  onPhaseComplete,
  submitting = false, 
  submitError = null, 
  hideSubmit = false,
  showPhaseComplete = false,
  phaseCompleteText = "Next Phase"
}) => {
  const { state, dispatch } = useQuizContext();
  const { questions = [], currentQuestionIndex = 0, responses = [] } = state;

  const currentQuestion = questions[currentQuestionIndex];

  const answeredCurrent = currentQuestion
    ? responses.some(r => r.questionId === currentQuestion._id && (r.selectedOptions?.length || 0) > 0)
    : false;

  const allAnswered = questions.length > 0 && responses.length >= questions.length;

  // ref to the question area so we can move focus on navigation
  const questionAreaRef = useRef(null);

  // focus the question area when currentQuestionIndex changes
  useEffect(() => {
    if (questionAreaRef.current) {
      // look for first focusable element in the question area
      const focusable = questionAreaRef.current.querySelector(
        'input, button, a, [tabindex]:not([tabindex="-1"])'
      );
      (focusable || questionAreaRef.current).focus?.();
    }
  }, [currentQuestionIndex]);

  const next = () => {
    if (currentQuestionIndex + 1 < questions.length) {
      dispatch({ type: 'SET_CURRENT_INDEX', payload: currentQuestionIndex + 1 });
    }
  };

  const prev = () => {
    if (currentQuestionIndex > 0) {
      dispatch({ type: 'SET_CURRENT_INDEX', payload: currentQuestionIndex - 1 });
    }
  };

  if (!questions || questions.length === 0) {
    return <div className="mwc-empty">No quiz questions available.</div>;
  }

  // Determine if we're on the last question
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  return (
    <section className="mwc-quiz-wrapper" aria-labelledby="quiz-title">
      {/* Current question */}
      {currentQuestion && (
        <div
          className="mwc-question-area"
          ref={questionAreaRef}
          tabIndex={-1}
        >
          <QuestionCard question={currentQuestion} />
        </div>
      )}

      {/* Controls */}
      <div className="mwc-quiz-controls" aria-hidden={false}>
        {currentQuestionIndex > 0 && (
          <button
            className="mwc-btn"
            type="button"
            onClick={prev}
            aria-label="Previous question"
          >
            Previous
          </button>
        )}

        {/* Show Next button if NOT on last question */}
        {!isLastQuestion && (
          <button
            className="mwc-btn mwc-btn--primary"
            type="button"
            onClick={next}
            disabled={!answeredCurrent || submitting}
            aria-disabled={!answeredCurrent || submitting}
            style={{ marginLeft: 12 }}
          >
            Next
          </button>
        )}

        {/* Show Phase Complete button when on last question AND showPhaseComplete is true */}
        {isLastQuestion && showPhaseComplete && (
          <button
            className="mwc-btn mwc-btn--primary"
            type="button"
            onClick={onPhaseComplete}
            disabled={!allAnswered || submitting}
            aria-disabled={!allAnswered || submitting}
            style={{ marginLeft: 12 }}
          >
            {phaseCompleteText}
          </button>
        )}

        {/* Show Submit button when on last question AND showPhaseComplete is false */}
        {isLastQuestion && !showPhaseComplete && (
          <div style={{ display: 'inline-block', marginLeft: 12 }}>
            <button
              className="mwc-btn mwc-btn--primary"
              type="button"
              onClick={onSubmit}
              disabled={submitting || !allAnswered}
              aria-disabled={submitting || !allAnswered}
              aria-busy={submitting}
              aria-label="Submit quiz"
              style={{
                cursor: (submitting || !allAnswered) ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        )}
      </div>

      {/* Submit error area (accessible) */}
      <div
        role="alert"
        aria-live="assertive"
        style={{ marginTop: 12, minHeight: 20 }}
      >
        {submitError && <div className="mwc-error" style={{ color: 'crimson' }}>{submitError}</div>}
      </div>
    </section>
  );
};

QuizWrapper.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onPhaseComplete: PropTypes.func,
  submitting: PropTypes.bool,
  submitError: PropTypes.string,
  hideSubmit: PropTypes.bool,
  showPhaseComplete: PropTypes.bool,
  phaseCompleteText: PropTypes.string
};

export default QuizWrapper;